import { CheckCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Table, tableNumericClass } from '@shared/ui';

import styles from './ComparisonTable.module.css';

/** Значение одной ячейки. `null` — «Нет данных», лучшим не бывает никогда. */
export interface ComparisonCell {
  /** Готовая строка: форматирование по локали делает вызывающий экран. */
  text: string | null;
  /** Число выравнивается по правому краю; текст — по левому (DS §5.6). */
  numeric?: boolean | undefined;
}

export interface ComparisonRow {
  /** Ключ i18n подписи параметра. */
  labelKey: string;
  cells: readonly ComparisonCell[];
  /**
   * Индексы лучших ячеек строки. Пусто — лучшего нет (или считать его нечем).
   *
   * Список, а не одно значение: при равных значениях выбрать «первого из
   * равных» произвольно нельзя — подсветка врала бы. Считает вызывающий по
   * данным backend: «лучше» зависит от направления (меньший пробег, больший
   * объём), и таблица его не угадывает. `NULL` лучшим не бывает — это
   * проверяется здесь ещё раз.
   */
  bestIndexes?: readonly number[] | undefined;
}

interface ComparisonTableProps {
  /** Заголовки колонок — названия автомобилей. До четырёх (MVP_PRD §34.4). */
  columns: readonly string[];
  rows: readonly ComparisonRow[];
  caption: string;
  /** Дополнительный класс контейнера: страница расставляет отступы сама. */
  containerClassName?: string | undefined;
}

const MAX_COLUMNS = 4;

/**
 * Таблица сравнения (FE-194, DESIGN_SYSTEM §5.6, референс `screen4`).
 *
 * Первая колонка закреплена, лучшее значение подсвечено **и** помечено
 * иконкой: одной подсветкой обходиться нельзя — цвет не может быть
 * единственным носителем смысла. На мобильном таблица скроллится внутри
 * своего контейнера, страница не едет.
 */
export function ComparisonTable({
  columns,
  rows,
  caption,
  containerClassName,
}: ComparisonTableProps) {
  const { t } = useTranslation();
  const visible = columns.slice(0, MAX_COLUMNS);
  const noData = t('catalog.noData');

  return (
    <Table
      stickyFirstColumn
      containerClassName={[styles.container, containerClassName].filter(Boolean).join(' ')}
    >
      <caption className="visually-hidden">{caption}</caption>
      <thead>
        <tr>
          <th scope="col">{t('catalog.compare.parameter')}</th>
          {/* Ключ — позиция, а не подпись: в четвёрке законно бывают две машины
              одной модели (MAX_SAME_MODEL_IN_TOP4 = 2), и подписи совпадают. */}
          {visible.map((column, index) => (
            <th key={index} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.labelKey}>
            <th scope="row" className={styles.rowLabel}>
              {t(row.labelKey)}
            </th>
            {visible.map((_column, index) => {
              const cell = row.cells[index];
              // «Нет данных» лучшим не бывает: подсветить пустую ячейку значило
              // бы объявить отсутствие информации преимуществом.
              const best = (row.bestIndexes?.includes(index) ?? false) && cell?.text != null;

              return (
                <td
                  key={index}
                  className={[
                    cell?.numeric === true ? tableNumericClass : null,
                    best ? styles.best : null,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {best ? (
                    <CheckCircleIcon
                      size={16}
                      weight="fill"
                      aria-hidden="true"
                      className={styles.bestIcon}
                    />
                  ) : null}
                  {cell?.text ?? noData}
                  {best ? (
                    <span className="visually-hidden"> — {t('catalog.compare.best')}</span>
                  ) : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
